import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import {
  FormProvider,
  type SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { Box } from '~/components/elements/Box'
import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { VStack } from '~/components/elements/VStack'
import { useLogEvent } from '~/hooks/useLogEvent'
import {
  ensureExists,
  exists,
  findByPath,
  mapItemsToObj,
  unassignedSymbol,
} from '~/utils'

import { dialogInfoAtom, linkAnswersInfoAtom } from '../../atoms'
import { useCloseDialogWithRefresh } from '../../hooks/useCloseDialogWithRefresh'
import { useUpdateQuestionsMutation } from '../../hooks/useUpdateQuestionsMutation'
import {
  LinkAnswersDialog,
  type LinkAnswersDialogProps,
} from '../LinkAnswersDialog'

import { QuestionInput } from './QuestionInput'
import { type InputValues, resolver } from './utils'

export const QuestionSettingsDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  const surveyGroup = ensureExists(
    dialogInfo?.surveyGroup,
    t('assertion_failed'),
  )
  const defaultQuestions = React.useMemo(
    () =>
      surveyGroup.questions.map((question) => ({
        ...R.pick(question, ['id', 'name', 'showCustomizedKPI']),
        attachedQuestionMap: mapItemsToObj(surveyGroup.surveys, (survey) => {
          const attachedQuestion = findByPath(
            question.attachedQuestions,
            'surveyId',
            survey.id,
          )

          return exists(attachedQuestion)
            ? (attachedQuestion.questionId ?? null)
            : unassignedSymbol
        }),
        options: question.options.map((option) => ({
          ...R.pick(option, ['id', 'text']),
          attachedOptionMap: mapItemsToObj(surveyGroup.surveys, (survey) => {
            const attachedOption = findByPath(
              option.attachedOptions,
              'surveyId',
              survey.id,
            )

            return exists(attachedOption)
              ? (attachedOption.option ?? null)
              : unassignedSymbol
          }),
        })),
      })) satisfies InputValues['questions'],
    [surveyGroup.questions, surveyGroup.surveys],
  )

  const methods = useForm<InputValues>({
    defaultValues: { questions: defaultQuestions },
    resolver,
  })
  const { control, handleSubmit, setValue } = methods
  const { append, fields, remove } = useFieldArray({
    control,
    keyName: 'fieldId',
    name: 'questions',
  })

  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const updateQuestionsMutation = useUpdateQuestionsMutation({
    onSuccess: closeDialogWithRefresh,
  })
  const resetDialogInfo = useResetAtom(dialogInfoAtom)

  const logEvent = useLogEvent()
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ questions }) => {
      logEvent('Define question setting', {
        eventProperties: questions.map((question, index) => ({
          key: t('questionnaire_index_1', { index }),
          value: question.name,
        })),
      })

      if (R.isDeepEqual(questions, defaultQuestions)) {
        resetDialogInfo()

        return
      }

      updateQuestionsMutation.mutate({
        questions,
        defaultQuestions,
        surveyGroupId: surveyGroup.id,
      })
    },
    [
      defaultQuestions,
      logEvent,
      resetDialogInfo,
      surveyGroup.id,
      t,
      updateQuestionsMutation,
    ],
  )

  const handleAddButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    append({
      name: '',
      attachedQuestionMap: mapItemsToObj(
        surveyGroup.surveys,
        () => unassignedSymbol,
      ),
      options: [],
      showCustomizedKPI: true,
    })
  }, [append, surveyGroup.surveys])

  const linkAnswersInfo = useAtomValue(linkAnswersInfoAtom)
  const handleLinkAnswersDialogExecute = React.useCallback<
    EH<LinkAnswersDialogProps, 'onExecute'>
  >(
    (index, options) => {
      setValue(`questions.${index}.options`, options)
    },
    [setValue],
  )

  return (
    <>
      <FormProvider {...methods}>
        <ExecuteCancelDialog
          executing={updateQuestionsMutation.isPending}
          mode="settings"
          title={t('surveygroup_title_questionnaire_settings', { surveyGroup })}
          width="100%"
          onCancel={resetDialogInfo}
          onExecute={handleSubmit(handleValid)}
        >
          <VStack spacing={0}>
            <Box padding={2}>
              <StyledButton icon="add" onClick={handleAddButtonClick}>
                {t('add_questionnaire')}
              </StyledButton>
            </Box>

            {fields.map((field, index) => (
              <QuestionInput
                key={field.fieldId}
                index={index}
                onDelete={remove}
              />
            ))}
          </VStack>
        </ExecuteCancelDialog>
      </FormProvider>

      {exists(linkAnswersInfo) && (
        <LinkAnswersDialog onExecute={handleLinkAnswersDialogExecute} />
      )}
    </>
  )
}
